import { useEffect, useState, useMemo, useCallback } from "react";
import { useTranslation } from "react-i18next";
import DataTable from "react-data-table-component";
import { http } from "../helper";
import { BaseUrl } from "../common/config";
import DatePicker from "react-datepicker";
import { format } from "date-fns";
import Popup from "../common/popup";
import { dateformatwithuktime } from "../utilityfunctions";

export const CustomerFirstOrders = ({}) => {
  const { t } = useTranslation();

  const priceFormat = useCallback((price) => {
    return price.toLocaleString("en-US", {
      style: "currency",
      currency: "GBP",
      minimumFractionDigits: 2,
    });
  }, []);

  const formatDate = useCallback((dateString) => {
    const dateObject = new Date(dateString);
    const year = dateObject.getFullYear();
    const month = String(dateObject.getMonth() + 1).padStart(2, "0");
    const day = String(dateObject.getDate()).padStart(2, "0");
    return `${day}/${month}/${year}`;
  }, []);

  const getAdjustedOnboardedDate = useCallback(
    (account) => {
      if (!account?.accountCreatedAt) return "";
      const originalDate = new Date(account.accountCreatedAt);
      const comparisonDate = new Date("2024-09-02");
      const chosenDate =
        originalDate < comparisonDate ? comparisonDate : originalDate;
      return formatDate(chosenDate);
    },
    [formatDate],
  );

  const getAdjustedActivatedDate = useCallback(
    (account) => {
      if (!account) return "";
      const originalDate = account.recentActivatedDate
        ? new Date(account.recentActivatedDate)
        : account.accountCreatedAt
          ? new Date(account.accountCreatedAt)
          : null;
      if (!originalDate) return "";
      const comparisonDate = new Date("2024-09-02");
      const chosenDate =
        originalDate < comparisonDate ? comparisonDate : originalDate;
      return formatDate(chosenDate);
    },
    [formatDate],
  );
  // State declarations
  var currentTime = new Date();
  var currentyear = currentTime.getFullYear();
  var currentMonth = currentTime.getUTCMonth() + 1;
  const [filter, setFilter] = useState(0);
  const [filtertext, setFilterText] = useState("");
  const [firstCustomerOrders, setFirstCustomerOrders] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [showDatePicker, setShowDatePicker] = useState(false);
  const [fromDate, setFromDate] = useState(null);
  const [toDate, setToDate] = useState(null);
  const [popupLogView, setPopupLogView] = useState(false);
  const [accountLogs, setAccountLogs] = useState([]); // Stores fetched logs for selected account
  const [accountCode, setAccountCode] = useState(""); // Stores the account code clicked
  const [isLoadingLogs, setIsLoadingLogs] = useState(false); // Loading state while fetching logs
  const [period, setPeriod] = useState("");
  const [months, setMonths] = useState(9);
  const [quarter, setQuarter] = useState("");
  // Fiscal helper functions
  const getCurrentFiscalYear = useCallback(() => {
    const today = new Date();
    const year = today.getFullYear();
    const month = today.getMonth() + 1;
    // Fiscal year starts in September
    const startYear = month >= 9 ? year : year - 1;
    const endYear = startYear + 1;
    return `${startYear}-${endYear}`;
  }, []);

  const getCurrentFiscalQuarter = useCallback(() => {
    const month = new Date().getMonth() + 1; // 1–12
    if (month >= 9 && month <= 11) return 1; // Sep–Nov
    if (month === 12 || month <= 2) return 2; // Dec–Feb
    if (month >= 3 && month <= 5) return 3; // Mar–May
    return 4; // Jun–Aug
  }, []);
  // Initialize period and quarter after defining getCurrentFiscalYear/Quarter
  useEffect(() => {
    setPeriod(getCurrentFiscalYear());
    setQuarter(getCurrentFiscalQuarter());
  }, [getCurrentFiscalYear, getCurrentFiscalQuarter]);

  const handleActivatedDateClick = useCallback((code) => {
    setAccountCode(code);
    setIsLoadingLogs(true);
    http
      .get(BaseUrl + "admin/accountlog/" + code)
      .then((response) => {
        setAccountLogs(response.data.data);
        setIsLoadingLogs(false);
        setPopupLogView(true); // Show the popup
      })
      .catch(() => {
        setAccountLogs([]);
        setIsLoadingLogs(false);
        setPopupLogView(true); // show popup with No logs available
      });
  }, []);

  const columns = useMemo(
    () => [
      {
        name: t("account_code_txt"),
        selector: (row) => row.accountCode,
        sortable: false,
      },
      {
        name: t("account_name_txt"),
        selector: (row) => row.accountName,
        sortable: false,
      },
      {
        name: t("date_onboarded_txt"),
        selector: (row) => row.orderCreatedAt,
        cell: (row) => getAdjustedOnboardedDate(row),
      },
      {
        name: t("activated_date_before_first_order_txt"),
        selector: (row) => row.recentActivatedDate || row.accountCreatedAt,
        cell: (row) => {
          const displayDate = getAdjustedActivatedDate(row);
          return displayDate ? (
            <a
              className="button"
              href="#"
              onClick={(e) => {
                e.preventDefault();
                handleActivatedDateClick(row.accountCode);
              }}
            >
              {displayDate}
            </a>
          ) : (
            ""
          );
        },
        sortable: true,
      },
      {
        name: t("ordered_date_txt"),
        selector: (row) => new Date(row.orderCreatedAt),
        cell: (row) => {
          if (!row.orderCreatedAt) return "";
          const originalDate = new Date(row.orderCreatedAt);
          const comparisonDate = new Date("2024-09-02");
          const displayDate =
            originalDate < comparisonDate ? comparisonDate : originalDate;
          return formatDate(displayDate);
        },
        sortable: true,
      },
      {
        name: t("ordered_amount_txt"),
        selector: (row) =>
          row?.OrderTotalWithVat ? priceFormat(row.OrderTotalWithVat) : 0,
        sortable: false,
      },
    ],[t,getAdjustedOnboardedDate,getAdjustedActivatedDate,handleActivatedDateClick,priceFormat]
  );

  const customStyles = {
    rows: {
      style: {
        minHeight: "25px",
      },
    },
    headCells: {
      style: {
        padding: "8px", // override the cell padding for head cells
      },
    },
    cells: {
      style: {
        padding: "8px", // override the cell padding for data cells
      },
    },
  };

  const paginationComponentOptions = {
    rowsPerPageText: "",
    rangeSeparatorText: "",
    selectAllRowsItem: true,
    selectAllRowsItemText: "",
    noRowsPerPage: "",
  };

  const MonthNames = useMemo(
    () => [
      t("months.sep"),
      t("months.oct"),
      t("months.nov"),
      t("months.dec"),
      t("months.jan"),
      t("months.feb"),
      t("months.mar"),
      t("months.apr"),
      t("months.may"),
      t("months.jun"),
      t("months.jul"),
      t("months.aug"),
    ],
    [t],
  );
  const quarterNames = useMemo(
    () => ({
      1: t("quarter_sep_nov"),
      2: t("quarter_dec_feb"),
      3: t("quarter_mar_may"),
      4: t("quarter_jun_aug"),
    }),
    [t],
  );

  const getAvailableQuarters = (selectedFiscalYear) => {
    const currentFiscalYear = getCurrentFiscalYear();
    const currentQuarter = getCurrentFiscalQuarter();
    return Object.entries(quarterNames)
      .filter(([quarterNumber]) => {
        if (selectedFiscalYear === currentFiscalYear) {
          return Number(quarterNumber) <= currentQuarter;
        }
        return true; // show all for past years
      })
      .map(([quarterNumber, label]) => (
        <option key={quarterNumber} value={quarterNumber}>
          {label}
        </option>
      ));
  };

  useEffect(() => {
    if (filter === "custom" && (!fromDate || !toDate)) {
      // Don't make the API call if dates are missing for the "custom" filter
      return;
    }
    setIsLoading(true);
    let payload = {};
    if (filter === "custom") {
      payload = {
        filter: filter,
        datefrom: format(new Date(fromDate), "yyyy-MM-dd"),
        dateto: format(new Date(toDate), "yyyy-MM-dd"),
      };
      setShowDatePicker(false);
    } else if (filter === "weekly") {
      payload = {
        filter: "weekly",
      };
    } else if (["monthly", "quarterly", "FYdata"].includes(filter)) {
      payload = {
        filter: filter,
        datefrom: "",
        dateto: "",
        period: period,
      };
      if (filter === "quarterly") {
        payload.value = Number(quarter);
      } else if (filter === "monthly") {
        payload.value = Number(months);
      }
    } else {
      payload = {
        filter: parseInt(filter),
        datefrom: "",
        dateto: "",
      };
    }
    let URL2 = BaseUrl + "admin/customerFirstOrderDetail";
    http
      .post(URL2, payload)
      .then((response) => {
        setIsLoading(false);
        console.log(response);
        const data = response.data.data;
        const sortedData = [...data].sort(
          (a, b) =>
            new Date(b.orderCreatedAt).getTime() -
            new Date(a.orderCreatedAt).getTime(),
        );
        setFirstCustomerOrders(sortedData);
      })
      .catch((err) => {
        setIsLoading(false);
        console.log(err);
      });
  }, [filter, fromDate, toDate, months, quarter, period]);

  const changeFilterHandler = useCallback((e) => {
    const val = e.target.value;
    setFilter(val);
    setFilterText(e.target.options[e.target.selectedIndex].text);
    if (val === "custom") {
      setShowDatePicker(true);
    } else {
      setShowDatePicker(false);
      setFromDate(null);
      setToDate(null);
    }
  }, []);

  const changePeriodHandler = (e) => {
    const newPeriod = e.target.value;
    setPeriod(newPeriod); // update selected fiscal year in state
    //split fiscal year
    const [newStartYear, newEndYear] = newPeriod.split("-").map(Number);
    const fiscalStartMonthIndex = 8; // 0-based index for September
    const today = new Date();
    const currentFiscalYear = getCurrentFiscalYear();
    //compare sleted year with currentfiscalyear
    const isCurrentFiscalYear = newPeriod === currentFiscalYear;
    // MONTHLY filter logic
    if (filter === "monthly") {
      let visibleMonthNames = MonthNames;
      if (isCurrentFiscalYear) {
        // Calculate current fiscal month index
        const currentMonthIndex = today.getMonth(); // 0-based
        const monthsPassedSinceFiscalStart =
          (currentMonthIndex - fiscalStartMonthIndex + 12) % 12;
        const maxVisibleIndex = monthsPassedSinceFiscalStart;
        visibleMonthNames = MonthNames.slice(0, maxVisibleIndex + 1);
      }
      const selectedMonthIndex = MonthNames.indexOf(months);
      const isValidMonth =
        selectedMonthIndex >= 0 &&
        selectedMonthIndex < visibleMonthNames.length;
      if (!isValidMonth) {
        const defaultMonthNumber = ((0 + fiscalStartMonthIndex) % 12) + 1;
        setMonths(String(defaultMonthNumber));
      }
    }
    // QUARTERLY filter logic
    if (filter === "quarterly") {
      const currentQuarter = getCurrentFiscalQuarter();
      let maxQuarter = 4;
      if (isCurrentFiscalYear) {
        maxQuarter = currentQuarter;
      }
      if (parseInt(quarter) > maxQuarter) {
        setQuarter(maxQuarter > 0 ? String(maxQuarter) : "1");
      }
    }
  };

  const changeMonthHandler = (e) => {
    setMonths(e.target.value);
  };

  const DateChange = (value, type) => {
    if (type === "from") {
      setFromDate(value);
      if (toDate && value && value > toDate) {
        setToDate(null);
      }
    } else if (type === "to") {
      if (!fromDate || value >= fromDate) {
        setToDate(value);
      } else {
        // Handle invalid date selection
        setToDate(null);
      }
    }
  };
  // Calculate dynamic pagination options
  const calculatePaginationOptions = (totalRecords, interval) => {
    const options = [];
    for (let i = interval; i <= totalRecords; i += interval) {
      options.push(i);
    }
    return options;
  };

  const totalRecords = firstCustomerOrders.length; // Get the total records count
  const paginationOptions = calculatePaginationOptions(totalRecords, 10); // Generate options with interval of 10
  const options = [];
  for (let i = currentyear; i >= 2023; i--) {
    if (i === currentyear && currentMonth < 9) {
      continue;
    }
    const year = i;
    options.push(
      <option value={year + "-" + (year + 1)}>
        {year + "-" + (year + 1)}
      </option>,
    );
  }

  const currentFiscalYear = getCurrentFiscalYear();
  let visibleMonthNames = MonthNames;
  if (period === currentFiscalYear) {
    // Limit to current fiscal month
    const fiscalIndex = (new Date().getMonth() - 8 + 12) % 12; // Sep=0, Oct=1, ... Aug=11
    visibleMonthNames = MonthNames.slice(0, fiscalIndex + 1);
  }
  let loadingbuffer = isLoading ? "loading-buffer" : "";

  return (
    <div
      className={`chart-container cust-first-orders ${loadingbuffer}`}
      style={{ verticalAlign: "top" }}
    >
      <div className="chart-Hdr">
        <div className="chart-Hdr-txt">
          <h2>{t("first_time_customer_order_txt")}</h2>
        </div>
        {/* New Sort Filter Dropdown */}
        <div className="chart-fltr-Hldr">
          <div
            className="chart-fltr"
            onMouseEnter={() => {
              if (filter === "custom" && !showDatePicker) {
                setShowDatePicker(true);
              }
            }}
            onMouseLeave={() => {
              if (filter === "custom" && showDatePicker) {
                setShowDatePicker(false);
              }
            }}
          >
            {
              <select value={filter} onChange={(e) => changeFilterHandler(e)}>
                <option value={0}>{t("all_txt")}</option>
                <option value={"weekly"}>{t("current_week_txt")}</option>
                <option value={"monthly"}>{t("monthly_txt")}</option>
                <option value={"quarterly"}>{t("quarterly_txt")}</option>
                <option value={"FYdata"}>{t("fy_data_txt")}</option>
                <option value={30}>{t("past_30_days_txt")}</option>
                <option value={90}>{t("past_90_days_txt")}</option>
                <option value={180}>{t("past_180_days_txt")}</option>
                <option value={365}>{t("past_1_year_txt")}</option>
                <option value={"custom"}>{t("custom_date_txt")}</option>
              </select>
            }
            {showDatePicker && (
              <div className="date-picker-container">
                <label>{t("from_txt")}:</label>
                <DatePicker
                  selected={fromDate}
                  onChange={(date) => DateChange(date, "from")}
                  showDisabledMonthNavigation
                  dateFormat="dd/MM/yyyy"
                  disablePast={true}
                  maxDate={new Date()}
                  placeholderText={t("select_date_txt")}
                />
                <label>{t("to_txt")}:</label>
                <DatePicker
                  selected={toDate}
                  showDisabledMonthNavigation
                  dateFormat="dd/MM/yyyy"
                  onChange={(date) => DateChange(date, "to")}
                  disablePast={true}
                  maxDate={new Date()}
                  placeholderText={t("select_date_txt")}
                />
              </div>
            )}
            {filter === "quarterly" && (
              <select
                style={{ width: "160px" }}
                value={quarter}
                onChange={(e) => setQuarter(Number(e.target.value))}
              >
                {getAvailableQuarters(period)}
              </select>
            )}
            {filter === "monthly" && (
              <select
                style={{ width: "50px" }}
                value={months}
                onChange={(e) => changeMonthHandler(e)}
              >
                {visibleMonthNames.map((monthName, index) => {
                  const monthNumber = ((index + 8) % 12) + 1; // fiscal-to-calendar month number
                  return (
                    <option key={monthNumber} value={monthNumber}>
                      {monthName}
                    </option>
                  );
                })}
              </select>
            )}
            {(filter === "monthly" ||
              filter === "quarterly" ||
              filter === "FYdata") && (
              <select value={period} onChange={(e) => changePeriodHandler(e)}>
                {options}
              </select>
            )}
          </div>
        </div>
        <div className="fltr-sub-val">
          {firstCustomerOrders.length > 0 && (
            <span className="fltr-val">
              {t("total_records", { count: firstCustomerOrders.length })}{" "}
              {[
                t("current_week_txt"),
                t("monthly_txt"),
                t("quarterly_txt"),
                t("fy_data_txt"),
                t("all_txt"),
              ].includes(filtertext)
                ? ""
                : filtertext !== "" && filtertext !== t("custom_date_txt")
                  ? " (" + filtertext + ")"
                  : filtertext === t("custom_date_txt") && fromDate && toDate
                    ? " (" +
                      formatDate(fromDate) +
                      " - " +
                      formatDate(toDate) +
                      ")"
                    : " (" + t("all_txt") + ")"}
            </span>
          )}
        </div>
      </div>
      <DataTable
        columns={columns}
        data={firstCustomerOrders}
        customStyles={customStyles}
        noDataComponent={
          <div className="no-records-found">
            {t("no_data_filtered_search_txt")}
          </div>
        }
        pagination
        paginationPerPage={10}
        paginationRowsPerPageOptions={[10, 20, 40, 60, 80, 100]}
        paginationComponentOptions={paginationComponentOptions}
      />
      {popupLogView && (
        <Popup
          extClass="info-poup show"
          closebtn={
            <span
              className="close"
              onClick={() => setPopupLogView(false)}
            ></span>
          }
          headMoreTxt={
            <div className="order-top">
              <h4>
                {t("customer_status_log_details_txt")} - {accountCode}
              </h4>
            </div>
          }
          bodyMoreTxt={
            <div className="bottom vendor-details acc-log">
              {isLoadingLogs ? (
                <div>{t("loading_txt")}</div>
              ) : (
                <>
                  {accountLogs &&
                    accountLogs.length > 0 &&
                    accountLogs.map((log, index) => {
                      const [action, notes] = log.noteText.split(/-(.+)/);
                      const status =
                        action.trim() === "activate"
                          ? t("activated_txt")
                          : t("deactivated_txt");

                      return (
                        <div key={index} className="info-item">
                          <div className="info-innr">
                            <div className="flex-log-hdr">
                              <p>
                                <span className="labeltxt acn-by">
                                  {status}
                                </span>
                              </p>
                              <div className="hdrlog-rit">
                                {log.createdBy && (
                                  <>
                                    <span className="log-user">
                                      {log.createdBy}
                                    </span>
                                    <span className="log-line">|</span>
                                  </>
                                )}
                                <span className="log-date">
                                  {dateformatwithuktime(log.createdAt)}
                                </span>
                              </div>
                            </div>
                            <p>
                              <span className="labeltxt log-reason">
                                {t("reason_txt")}:{" "}
                              </span>
                              {notes ? notes.trim() : ""}
                            </p>
                          </div>
                        </div>
                      );
                    })}
                  {(() => {
                    const account = firstCustomerOrders.find(
                      (acc) => acc.accountCode === accountCode,
                    );
                    if (account) {
                      const onboarded = getAdjustedOnboardedDate(account);
                      const activated = getAdjustedActivatedDate(account);
                      return (
                        <div className="info-item">
                          <div className="info-innr">
                            <p>
                              {onboarded === activated
                                ? `${t("onboarded_activated_on_txt")} (${onboarded})`
                                : `${t("onboarded_activated_on_txt")} (${onboarded} & ${activated})`}
                            </p>
                          </div>
                        </div>
                      );
                    }
                    return null;
                  })()}
                </>
              )}
            </div>
          }
        />
      )}
    </div>
  );
};
